import { useCallback, useEffect, useRef, useState } from "react";
import { DrawSignIcon, TypeSignIcon } from "../../icon";

const SignaturePad: React.FC<{
    onSave: (signature: string) => void;
    onCancel: () => void;
    existingSignature?: string;
}> = ({ onSave, onCancel, existingSignature }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [hasExistingSignature, setHasExistingSignature] = useState(false);
    const [activeTab, setActiveTab] = useState<'type' | 'draw' | 'upload'>('type');
    const [typedSignature, setTypedSignature] = useState('');
    const [selectedFont, setSelectedFont] = useState('cursive');
    
    const [hasLoadedExisting, setHasLoadedExisting] = useState(false);

    const detectSignatureType = useCallback((signature: string): 'type' | 'draw' => {
        if (!signature) return 'type';
        
        if (signature.startsWith('data:image/')) {
            return 'draw';
        }
        
        return 'type';
    }, []);

    const setupCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const rect = canvas.getBoundingClientRect();
        
        canvas.width = rect.width;
        canvas.height = rect.height;

        canvas.style.width = `${rect.width}px`;
        canvas.style.height = `${rect.height}px`;

        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = '#000';
        ctx.imageSmoothingEnabled = true;

        if (existingSignature && existingSignature.startsWith('data:image/') && activeTab === 'draw' && !hasLoadedExisting) {
            const img = new Image();
            img.onload = () => {
                ctx.clearRect(0, 0, canvas.width, canvas.height);
                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                setHasExistingSignature(true);
                setHasLoadedExisting(true);
            };
            img.src = existingSignature;
        }
    }, [existingSignature, activeTab, hasLoadedExisting]);

    const getCoordinates = (e: React.MouseEvent | React.TouchEvent): { x: number; y: number } => {
        const canvas = canvasRef.current;
        if (!canvas) return { x: 0, y: 0 };

        const rect = canvas.getBoundingClientRect();
        let clientX: number, clientY: number;

        if ('touches' in e) {
            const touch = e.touches[0] || e.changedTouches[0];
            clientX = touch.clientX;
            clientY = touch.clientY;
        } else {
            clientX = e.clientX;
            clientY = e.clientY;
        }

        return {
            x: clientX - rect.left,
            y: clientY - rect.top
        };
    };

    useEffect(() => {
        if (existingSignature) {
            const signatureType = detectSignatureType(existingSignature);
            setActiveTab(signatureType);
        }
    }, [existingSignature, detectSignatureType]);

    useEffect(() => {
        setupCanvas();

        const handleResize = () => {
            setTimeout(setupCanvas, 100);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [setupCanvas]);

    useEffect(() => {
        setHasLoadedExisting(false);
        if (activeTab === 'draw') {
            setTimeout(() => setupCanvas(), 100);
        }
    }, [activeTab]);

    const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
        e.preventDefault();

        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const { x, y } = getCoordinates(e);

        ctx.beginPath();
        ctx.moveTo(x, y);
        setIsDrawing(true);

        if (hasExistingSignature) {
            setHasExistingSignature(false);
        }
    };

    const draw = (e: React.MouseEvent | React.TouchEvent) => {
        e.preventDefault();

        if (!isDrawing) return;

        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const { x, y } = getCoordinates(e);

        ctx.lineTo(x, y);
        ctx.stroke();
    };

    const stopDrawing = (e?: React.MouseEvent | React.TouchEvent) => {
        if (e) e.preventDefault();
        setIsDrawing(false);
    };

    const clearSignature = () => {
        if (activeTab === 'draw') {
            const canvas = canvasRef.current;
            if (!canvas) return;
            
            const ctx = canvas.getContext('2d');
            if (!ctx) return;
            
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            setHasExistingSignature(false);
            setHasLoadedExisting(false);
        } else if (activeTab === 'type') {
            setTypedSignature('');
        }
    };

    const generateTypedSignature = (): string => {
        const tempCanvas = document.createElement('canvas');
        const tempCtx = tempCanvas.getContext('2d');
        if (!tempCtx) return '';

        const fontSize = 320;
        tempCtx.font = `${fontSize}px ${selectedFont}`;
        
        const metrics = tempCtx.measureText(typedSignature);
        const textWidth = metrics.width;
        const textHeight = fontSize * 1.5; 
        
        const canvas = document.createElement('canvas');
        const padding = 100; 
        canvas.width = Math.max(textWidth + (padding * 2), 800); 
        canvas.height = Math.max(textHeight + (padding * 2), 400); 
        
        const ctx = canvas.getContext('2d');
        if (!ctx) return '';

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        ctx.fillStyle = '#000000';
        ctx.font = `${fontSize}px ${selectedFont}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(typedSignature, canvas.width / 2, canvas.height / 2);

        return canvas.toDataURL('image/png')
    };

    const saveSignature = () => {
        let signatureData: string;

        if (activeTab === 'type') {
            if (!typedSignature.trim()) {
                alert('Please type your signature before saving.');
                return;
            }
            signatureData = generateTypedSignature();
        } else if (activeTab === 'draw') {
            const canvas = canvasRef.current;
            if (!canvas) return;
            
            const ctx = canvas.getContext('2d');
            if (!ctx) return;
            
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const isEmpty = !imageData.data.some((channel, index) => {
                return index % 4 === 3 && channel !== 0;
            });

            if (isEmpty && !hasExistingSignature) {
                alert('Please draw your signature before saving.');
                return;
            }
            
            signatureData = canvas.toDataURL('image/png');
        } else {
            const canvas = canvasRef.current;
            if (!canvas) return;
            signatureData = canvas.toDataURL('image/png');
        }

        onSave(signatureData);
    };

    return (
        <div className="signature-overlay">
            <div className="signature-container" >
                <div className="h2-semibold color-primary signature-modal-title">
                    {existingSignature ? 'Update your signature' : 'Add your signature'}
                </div>

                <div className="signature-modal-tabs">
                    <button
                        onClick={() => setActiveTab('type')}
                        className="signature-modal-tab-btn d-flex flex-direction align-center h4-medium"
                        style={{
                            borderBottom: activeTab === 'type' ? '3px solid #3b82f6' : '3px solid transparent',
                            color: activeTab === 'type' ? '#3b82f6' : '#6b7280',
                        }}
                    >
                        <span><TypeSignIcon/></span>
                        TYPE
                    </button>
                    <button
                        onClick={() => setActiveTab('draw')}
                        className="signature-modal-tab-btn d-flex flex-direction align-center h4-medium"
                        style={{
                            borderBottom: activeTab === 'draw' ? '3px solid #3b82f6' : '3px solid transparent',
                            color: activeTab === 'draw' ? '#3b82f6' : '#6b7280',
                        }}
                    >
                        <span className=""><DrawSignIcon/></span>
                        DRAW
                    </button>
                </div>

                <div className="signature-modal-content">
                    {activeTab === 'type' && (
                        <div>
                            <div className="mb-2">
                                <label className="d-block mb-1 h4-medium color-secondary-light" >
                                    Type your signature:
                                </label>
                                <input
                                    type="text"
                                    value={typedSignature}
                                    onChange={(e) => setTypedSignature(e.target.value)}
                                    placeholder="Enter your name"
                                    style={{
                                        border: '2px solid #e5e7eb',
                                    }}
                                    className="signature-text-input"
                                />
                            </div>

                            {typedSignature && (
                                <div className="signature-preview-box d-flex align-center justify-center ">
                                    <div className="signature-preview-text">
                                        {typedSignature}
                                    </div>
                                </div>
                            )}
                            
                            {!typedSignature && existingSignature && activeTab === 'type' && (
                                <div className="signature-existing-preview">
                                    <p className="h5-medium color-primary-accent-alt mb-1">
                                        Current signature:
                                    </p>
                                    <img 
                                        src={existingSignature} 
                                        alt="Current signature" 
                                        className="signature-existing-image"
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'draw' && (
                        <div>
                            <p className="color-secondary-light h4-medium signature-draw-instructions">
                                {existingSignature ? 'Your current signature is shown below. Draw to update it:' : 'Draw your signature below:'}
                            </p>
                            <canvas
                                ref={canvasRef}
                                className="signature-canvas"
                                style={{width: "97%"}}
                                width={600}
                                height={200} 
                                onMouseDown={startDrawing}
                                onMouseMove={draw}
                                onMouseUp={stopDrawing}
                                onMouseLeave={stopDrawing}
                                onTouchStart={startDrawing}
                                onTouchMove={draw}
                                onTouchEnd={stopDrawing}
                                onTouchCancel={stopDrawing}
                            />
                        </div>
                    )}

                </div>

                <div className="d-flex justify-flex-end" style={{
                    gap: '12px',
                }}>
                    <button
                        onClick={clearSignature}
                        className="h4-medium color-grid-header-font-color bg-color-secondary signature-btn"
                    >
                        Clear
                    </button>
                    <button
                        onClick={onCancel}
                        className="h4-medium color-grid-header-font-color bg-color-secondary signature-btn"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={saveSignature}
                        className="h4-medium color-secondary bg-color-secondary-btn-color signature-button-primary c-pointer"
                    >
                        {existingSignature ? 'Update Signature' : 'Save Signature'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SignaturePad;